import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { jwtDecode } from "jwt-decode";
import { Observable, mergeMap, catchError, throwError } from "rxjs";
import { AbstractDataService } from "../abstract-data-service";
import { AppConfigService } from "../app-config.service";
import { AppUnauthorizedError } from "../common/errors";
import { ClientInfoModel, AuthUserModel, CargoFeaturesModel, AuthResult, JWTTokenModel, ClientInfo_RegistrationModel } from "../model";


@Injectable()
export class AuthService extends AbstractDataService {
  public user: ClientInfoModel | null | undefined = null;
  public authUser: AuthUserModel | null = null;
  private features: CargoFeaturesModel | null | undefined = null;
  private authTimer: any = null;

  constructor(http: HttpClient, router: Router, config: AppConfigService) {
    super(http, router, config);

    this.tryAutoLogin().then((res) => {
      console.log(`AUTOLOGIN: ${res}`);
    });
  }

  private async tryAutoLogin(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      const user_data = localStorage.getItem("user_data");
      if (user_data) {
        try {
          this.authUser = JSON.parse(user_data);
          resolve(await this.refreshLogin());
        } catch (err) {
          reject(err);
        }
      } else {
        resolve(false);
      }
    });
  }

  private refreshLogin(): Promise<boolean> {
    const safetyDelayFrame = 10 * 1000; // 10 seconds for safety time frame before key token will exipre

    return new Promise(async (resolve, reject) => {
      if (this.authUser && this.authUser.token && this.authUser.refresh && this.authUser.expiryDate) {

        // token expired, try to refresh
        if (this.authUser.expiryDate * 1000 - safetyDelayFrame < Date.now()) {
          const subscr = this.requestData("auth/refresh", {}, "post", this.authUser.refresh).subscribe({
            next: (data: AuthResult) => {
              const token_data = jwtDecode<JWTTokenModel>(data.token);

              this.authUser!.token = data.token;
              this.authUser!.refresh = data.refresh;
              this.authUser!.expiryDate = token_data.exp;
              this.authUser!.userId = token_data.userId;
              this.authUser!.userRole = token_data.userRole;
              this.storeUserData();
              this.autoRefreshLogin();

              console.log(`token refreshed: ${data.token}`);
            },
            error: async (err: Error) => {
              // check error on 'refresh_token_error'
              subscr.unsubscribe();

              const data = (err as AppUnauthorizedError).errData;
              console.log("REFRESH TOKEN ERROR: ", err.message);
              console.log("REFRESH TOKEN ERROR DATA: ", data);
              await this.logout();
              reject(err);
            },
            complete: () => {
              subscr.unsubscribe();
              resolve(true);
            }
          });
        } else {
          this.autoRefreshLogin();
          resolve(true);
        }
      } else {
        await this.logout();
        resolve(false);
      }
    });
  }

  private autoRefreshLogin() {
    if (this.authTimer) {
      clearTimeout(this.authTimer);
      this.authTimer = null;
    }

    if (this.authUser && this.authUser.expiryDate) {
      const intervalValue = this.authUser.expiryDate * 1000 - Date.now();
      this.authTimer = setTimeout(
        async () => {
          await this.refreshLogin();
        },
        intervalValue,
      );
    }
  }

  public getTokenRx(): Observable<string | null> {
    return new Observable((observer) => {
      if (this.authUser && this.authUser.token && this.authUser.refresh && this.authUser.expiryDate) {
        if (this.authUser.expiryDate * 1000 > Date.now()) {
          observer.next(this.authUser.token);
          observer.complete();
        } else {
          this.refreshLogin().then(() => {
            observer.next(this.authUser?.token);
            observer.complete();
          })
        }
      } else {
        observer.next(null);
        observer.complete();
      }
    });
  }

  private storeUserData() {
    if (this.authUser) {
      localStorage.setItem("user_data", JSON.stringify(this.authUser));
    }
  }

  public requestSecuredData(methodName: string, params: any = {}, methodType: string = "get"): Observable<any> {
    return this.getTokenRx()
      .pipe(
        mergeMap((token: string | null) => {
          return this.requestData(methodName, params, methodType, token);
        }),
        catchError((error: any) => {
          switch (error.status) {
            case 403: {
              this.logout().then(b => this.logger.info('Exception logout', b));
            }
          }
          return throwError(() => error)
        })
      )
  }

  /**
   * Get client info
   * @returns 
   */
  public userData(): Promise<ClientInfoModel> {
    return new Promise<ClientInfoModel>((result, error) => {
      if (this.user) {
        result(this.user);
      } else {
        const userDataSubscribe = this.requestData("app/UserInfo", {}, "get", this.authUser?.token).subscribe({
          next: (userData: ClientInfoModel) => {
            this.user = userData;
            this.logger.User = userData
            result(userData);
          },
          error: (err) => {
            userDataSubscribe.unsubscribe();
            error(err);
          },
          complete: () => {
            userDataSubscribe.unsubscribe();
          }
        });
      }
    });
  }

  public cargoFeatures(): Promise<CargoFeaturesModel> {
    return new Promise<CargoFeaturesModel>((result, error) => {
      if (this.features) {
        result(this.features);
      } else {
        const featuresSubscription = this.requestData(`app/features/${this.config.getConfig().cargoId}`, {}, 'get').subscribe({
          next: (featuresData: any) => {
            this.features = { ...{ daysToExpiration: featuresData.days_to_expiration }, ...JSON.parse(featuresData.features) };
            result(this.features!);
          },
          error: (err) => {
            error(err);
          },
          complete: () => {
            featuresSubscription.unsubscribe();
          }
        });
      }
    });
  }

  public login(login: string, passwd: string): Observable<AuthUserModel> {
    return new Observable((observer) => {

      let params: any = {};
      params.login = login;
      params.passwd = passwd;

      if (this.config && this.config.getConfig().cargoId) {
        params.cargo_id = this.config.getConfig().cargoId;
      }

      const subscr = this.requestData("auth/login", params, "post").subscribe({
        next: (result: AuthResult) => {
          const token_data = jwtDecode<JWTTokenModel>(result.token);

          if (!this.authUser) {
            this.authUser = {} as AuthUserModel;
          }

          this.authUser.token = result.token;
          this.authUser.refresh = result.refresh;
          this.authUser.expiryDate = token_data.exp;
          this.authUser.userId = token_data.userId;
          this.authUser.userRole = token_data.userRole;

          this.storeUserData();
          this.autoRefreshLogin();
          observer.next(this.authUser);
          observer.complete();
        },
        error: (err) => {
          subscr.unsubscribe();
          observer.error(err);
        },
        complete: () => {
          subscr.unsubscribe();
        }
      })
    });
    // return this.requestData("auth/login", params, "post");
  }

  public logout(): Promise<boolean> {

    return new Promise((resolve, reject) => {
      if (this.authTimer) {
        clearInterval(this.authTimer);
        this.authTimer = null;
      }

      if (this.authUser && this.authUser.token && this.authUser.refresh) {
        const subscr = this.requestData("auth/logout", { refresh_token: this.authUser.refresh }, "post", this.authUser.token).subscribe({
          next: () => { },
          error: (err) => {
            subscr.unsubscribe();
            reject(err);
          },
          complete: () => {
            subscr.unsubscribe();
            resolve(true);
          }
        });
      }

      this.authUser = null;
      localStorage.removeItem("user_data");

      if (this.authTimer) {
        clearTimeout(this.authTimer);
        this.authTimer = null;
      }

      this.user = null;
      this.features = null;
      this.logger.User = null;
      this.router.navigate(["/login"]);
    });
  }

  /**
   * Register new client
   * 
   * @param params 
   * @returns 
   */
  registerClient(params: ClientInfo_RegistrationModel): Observable<any> {
    return this.requestSecuredData('auth/registerClient', params, 'put');
  }

  /**
   * Update client's data
   * 
   * @param params 
   * @returns 
   */
  updateClient(params: ClientInfo_RegistrationModel): Observable<any> {
    return this.requestSecuredData("auth/updateClient", params, "post");
  }

  /**
   * Remove client and all related data
   * @param clientId ID of the client for deletion
   * @returns 
   */
  public deleteClient(clientId: number) {
    return this.requestSecuredData(`auth/deleteClient`, { client_id: clientId }, 'delete');
  }

  /**
   * Check if login exists
   * 
   * @param login 
   * @returns 
   */
  isLoginExists(cargo_id: number, login: string): Observable<any> {
    return this.requestData(`auth/isDataExists`, { cargo_id, login }, "get");
  }

  /**
   * Check if email exists
   * 
   * @param email 
   * @returns 
   */
  isEmailExists(cargo_id: number, email: string): Observable<any> {
    return this.requestData(`auth/isDataExists`, { cargo_id, email }, "get");
  }

  /**
   * Check if client_code exists
   * @param client_code 
   * @returns 
   */
  isClientExists(cargo_id: number, client_code: string): Observable<any> {
    return this.requestData(`auth/isDataExists`, { cargo_id, client: client_code }, "get");
  }

  resetPassword(login: string) {
    return this.requestData('auth/forgottenPassword', { login: login }, 'get');
  }

  resetPasswordByAdmin(login: string, passwd: string) {
    return this.requestSecuredData('auth/resetPasswordByAdmin', { login: login, passwd: passwd }, 'post');
  }

  /**
   * Change client passwdord
   * 
   * @param oldPasswd 
   * @param newPasswd 
   * @returns 
   */
  changePassword(oldPasswd: string, newPasswd: string) {
    return this.requestSecuredData(`auth/changePassword`, { old_passwd: oldPasswd, new_passwd: newPasswd }, 'post');
  }

  public getOTPCode(): Observable<String> {
    return this.requestSecuredData('auth/otpCode', {}, 'get');
}

}
